import React from 'react';
import { Loader2, Sparkles, ImageIcon } from 'lucide-react';

/**
 * BgCompareModal — after uploading a photo, the backend returns both the
 * raw upload and the AI-adjusted version; this modal lets the user pick
 * which one to apply as the slide background.
 */
export default function BgCompareModal({
  open,
  pageIndex,
  original,
  adjusted,
  creditsCharged,
  busy, // null | 'original' | 'adjusted'
  onPick,
  onCancel,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid={`bg-compare-modal-${pageIndex}`}
    >
      <div className="w-full max-w-2xl rounded-2xl border border-obsidian-600 bg-obsidian-950 p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3 className="text-white text-base font-semibold">選擇第 {pageIndex} 頁的背景</h3>
          <p className="text-obsidian-400 text-xs mt-1">
            已依照這一頁的版面與配色，把你上傳的圖片調整成更合適的底圖
            {creditsCharged > 0 && ` · 已扣除 ${Number(creditsCharged).toFixed(1)} 點數`}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <figure className="space-y-1.5">
            <figcaption className="flex items-center gap-1.5 text-[11px] text-obsidian-300">
              <ImageIcon className="w-3 h-3" /> 原圖
            </figcaption>
            <img
              src={original}
              alt="原圖"
              className="w-full aspect-[4/5] object-cover rounded-lg border border-obsidian-700"
              data-testid={`bg-compare-original-${pageIndex}`}
            />
          </figure>
          <figure className="space-y-1.5">
            <figcaption className="flex items-center gap-1.5 text-[11px] text-lime-400">
              <Sparkles className="w-3 h-3" /> 智能調整後
            </figcaption>
            <img
              src={adjusted}
              alt="智能調整後"
              className="w-full aspect-[4/5] object-cover rounded-lg border border-lime-500/50"
              data-testid={`bg-compare-adjusted-${pageIndex}`}
            />
          </figure>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={!!busy}
            className="h-9 px-4 rounded-md border border-obsidian-600 text-obsidian-300 text-xs hover:border-obsidian-500 transition-colors disabled:opacity-40"
            data-testid={`bg-compare-cancel-${pageIndex}`}
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => onPick('original')}
            disabled={!!busy}
            className="flex-1 h-9 rounded-md border border-obsidian-500 text-obsidian-100 text-xs font-medium hover:bg-obsidian-800 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40"
            data-testid={`bg-compare-use-original-${pageIndex}`}
          >
            {busy === 'original' && <Loader2 className="w-3 h-3 animate-spin" />}
            使用原圖
          </button>
          <button
            type="button"
            onClick={() => onPick('adjusted')}
            disabled={!!busy}
            className="flex-1 h-9 rounded-md bg-lime-500 hover:bg-lime-400 text-obsidian-950 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40"
            data-testid={`bg-compare-use-adjusted-${pageIndex}`}
          >
            {busy === 'adjusted' ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3" />
            )}
            使用調整後
          </button>
        </div>
      </div>
    </div>
  );
}
